import {
  BIG_ONE,
  BIG_TWO,
  BIG_ZERO,
  isqrtBig,
  pow10Big,
} from "@/lib/tool-kit/math/bigint";

/**
 * Fibonacci numbers on BigInt, with F(0) = 0 and F(1) = 1.
 *
 * Single terms use the fast-doubling identities
 *   F(2k)     = F(k) × (2·F(k+1) − F(k))
 *   F(2k + 1) = F(k)² + F(k+1)²
 * which need only about log₂ n multiplications, so F(100000), a number of 20,899 digits,
 * takes a few milliseconds. Ranges use plain addition.
 */

export const MAX_INDEX = 100_000;
export const MAX_COUNT = 100;
export const RATIO_DECIMALS = 30;

/** Binet's constants in double precision. */
const SQRT_5 = Math.sqrt(5);
export const PHI = (1 + SQRT_5) / 2;
export const PSI = (1 - SQRT_5) / 2;

/** [F(n), F(n + 1)] by fast doubling. */
export function fibonacciPair(n: number): [bigint, bigint] {
  if (n === 0) {
    return [BIG_ZERO, BIG_ONE];
  }
  const [a, b] = fibonacciPair(Math.floor(n / 2));
  const c = a * (BIG_TWO * b - a);
  const d = a * a + b * b;
  return n % 2 === 0 ? [c, d] : [d, c + d];
}

export function fibonacci(n: number): bigint {
  return fibonacciPair(n)[0];
}

/** F(from), F(from + 1), … for `count` terms. */
export function fibonacciRange(from: number, count: number): bigint[] {
  const terms: bigint[] = [];
  let [current, next] = fibonacciPair(from);
  for (let i = 0; i < count; i += 1) {
    terms.push(current);
    [current, next] = [next, current + next];
  }
  return terms;
}

/** The decimal digits of F(n), estimated from n·log₁₀φ − log₁₀√5 (exact for n ≥ 2). */
export function estimatedDigits(n: number): number {
  if (n < 2) {
    return 1;
  }
  return Math.floor(n * Math.log10(PHI) - Math.log10(SQRT_5)) + 1;
}

export interface BinetResult {
  phiPower: number;
  psiPower: number;
  /** φⁿ ÷ √5 — the dominant term. */
  dominant: number;
  /** (φⁿ − ψⁿ) ÷ √5, rounded to the nearest whole number. */
  rounded: number;
  /** Whether the double-precision answer equals the exact F(n). */
  exact: boolean;
}

/** Binet's formula in ordinary floating point, compared with the exact term. */
export function binet(n: number, exactValue: bigint): BinetResult {
  const phiPower = PHI ** n;
  const psiPower = PSI ** n;
  const rounded = Math.round((phiPower - psiPower) / SQRT_5);
  return {
    phiPower,
    psiPower,
    dominant: phiPower / SQRT_5,
    rounded,
    exact: Number.isFinite(rounded) && BigInt(rounded) === exactValue,
  };
}

/** Largest n for which the floating-point formula stays finite. */
export const MAX_BINET_INDEX = 1000;

/** a ÷ b truncated (not rounded) to `decimals` places, as "1.6180…". */
export function decimalQuotient(
  a: bigint,
  b: bigint,
  decimals: number
): string {
  const scaled = (a * pow10Big(decimals)) / b;
  const digits = scaled.toString().padStart(decimals + 1, "0");
  const point = digits.length - decimals;
  return `${digits.slice(0, point)}.${digits.slice(point)}`;
}

/** The golden ratio φ = (1 + √5) ÷ 2, truncated to `decimals` places. */
export function goldenRatio(decimals: number): string {
  const scale = pow10Big(decimals);
  const scaled = (scale + isqrtBig(BigInt(5) * scale * scale)) / BIG_TWO;
  return decimalQuotient(scaled, scale, decimals);
}

/** How many digits after the decimal point two decimal strings share. */
export function agreeingDecimals(a: string, b: string): number {
  const left = a.split(".")[1] ?? "";
  const right = b.split(".")[1] ?? "";
  if (a.split(".")[0] !== b.split(".")[0]) {
    return 0;
  }
  let shared = 0;
  while (shared < left.length && left[shared] === right[shared]) {
    shared += 1;
  }
  return shared;
}

/* ------------------------------------------------------------- reading */

export type IndexRead =
  | { kind: "ok"; value: number }
  | { kind: "empty" | "invalid" | "negative" | "too-large" };

const SEPARATORS = /[\s_  ]+/g;
const DIGITS = /^\d+$/;

export function readIndex(raw: string, max: number = MAX_INDEX): IndexRead {
  const text = raw.replace(SEPARATORS, "");
  if (text === "") {
    return { kind: "empty" };
  }
  if (text.startsWith("-") || text.startsWith("−")) {
    return DIGITS.test(text.slice(1))
      ? { kind: "negative" }
      : { kind: "invalid" };
  }
  if (!DIGITS.test(text)) {
    return { kind: "invalid" };
  }
  const value = Number(text);
  return value > max ? { kind: "too-large" } : { kind: "ok", value };
}

/** A long integer as its first and last digits plus the digit count: 1234…6789 (40). */
export function abbreviateDigits(value: bigint, limit = 40, keep = 14): string {
  const text = value.toString();
  if (text.length <= limit) {
    return text;
  }
  return `${text.slice(0, keep)}…${text.slice(-keep)} (${text.length})`;
}
